import ContactSvg from "@/assets/icons/contacts.svg?react";
import CustomerRequestSvg from "@/assets/icons/customerRequest.svg?react";
import HeadLogoSvg from "@/assets/icons/headLogo.svg?react";
import LicenseSvg from "@/assets/icons/license.svg?react";
import LogoutSvg from "@/assets/icons/logout.svg?react";
import ProductSvg from "@/assets/icons/products.svg?react";
import UserSvg from "@/assets/icons/user.svg?react";
import { LocalStorage } from "@/common/storage.ts";
import ConfirmButton, {
  ConfirmButtonRef,
} from "@/component/common/button/ConfirmButton.tsx";
import { SwitchLangButton } from "@/component/common/button/SwitchLangButton.tsx";
import UserHeaderButton from "@/component/common/button/UserHeaderButton.tsx";
import { MANAGER_PATH } from "@/constant/url-path.ts";
import i18n from "@/i18n/i18n.ts";
import { AuthService } from "@/services/auth/AuthService.ts";
import Icon, { CloudServerOutlined } from "@ant-design/icons";
import { Flex, Layout, Menu, MenuProps } from "antd";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

const { Header, Content, Sider } = Layout;

type MenuItem = Required<MenuProps>["items"][number];

const renderItemsSide = (): MenuItem[] => [
  {
    key: "license",
    label: i18n.t("field.LICENSE"),
    type: "group",
    children: [
      {
        key: MANAGER_PATH.LICENSE_REGISTRATION.LIST,
        label: i18n.t("field.license"),
        icon: <Icon component={LicenseSvg} style={{ fontSize: 20 }} />,
      },
      {
        key: MANAGER_PATH.PRODUCT.LIST,
        label: i18n.t("field.products"),
        icon: <Icon component={ProductSvg} style={{ fontSize: 20 }} />,
      },
    ],
  },
  {
    key: "manage",
    label: i18n.t("field.MANAGE"),
    type: "group",
    children: [
      {
        key: MANAGER_PATH.CONTACT.LIST,
        label: i18n.t("field.customerInquiries"),
        icon: <Icon component={ContactSvg} style={{ fontSize: 20 }} />,
      },
      {
        key: MANAGER_PATH.CUSTOMER.REQUEST,
        label: i18n.t("field.customerRequest"),
        icon: <Icon component={CustomerRequestSvg} style={{ fontSize: 20 }} />,
      },
    ],
  },
  {
    key: "transcoder",
    label: i18n.t("field.TRANSCODER"),
    type: "group",
    children: [
      {
        key: MANAGER_PATH.CONVERSION_RECORD.LIST,
        label: i18n.t("field.conversion_record"),
        icon: <CloudServerOutlined style={{ fontSize: 20 }} />,
      },
    ],
  },
  {
    key: "setting",
    label: i18n.t("field.SETTING"),
    type: "group",
    children: [
      {
        key: MANAGER_PATH.USER.LIST,
        label: i18n.t("field.user"),
        icon: <Icon component={UserSvg} style={{ fontSize: 20 }} />,
      },
      {
        key: "manage/logout",
        label: i18n.t("button.logout"),
        icon: <Icon component={LogoutSvg} style={{ fontSize: 20 }} />,
      },
    ],
  },
];

const ManagePageLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const logoutConfirmModalRef = useRef<ConfirmButtonRef>();
  const allMenuItems = renderItemsSide();

  useEffect(() => {
    if (location?.pathname === MANAGER_PATH.MANAGE)
      navigate(MANAGER_PATH.LICENSE_REGISTRATION.LIST);
  }, [navigate, location?.pathname]);

  const onSelectMenu = (key: string) => {
    if (key === "manage/logout") {
      logoutConfirmModalRef.current?.openModal();
    } else {
      navigate(key);
    }
  };

  const logout = async () => {
    await AuthService.signOut();
    LocalStorage.clearAuthenticationStorage();
    navigate(MANAGER_PATH.AUTH.SIGN_IN);
  };

  return (
    <>
      <Layout className={"min-h-screen max-h-screen overflow-hidden"}>
        <Header
          className={"flex items-center top-0"}
          style={{
            borderBottom: "1px solid #E8E9EB",
          }}
        >
          <Icon
            component={HeadLogoSvg}
            style={{ fontSize: 24, marginRight: "1rem" }}
          />
          <div className="flex-1 text-[#22242C] font-bold text-size-1 h-full">
            {t("title.licenseManagementLogo")}
          </div>
          <Flex justify={"flex-end"} align={"center"}>
            <SwitchLangButton />
            <UserHeaderButton logoutModalRef={logoutConfirmModalRef.current} />
          </Flex>
        </Header>
        <Layout>
          <Sider
            width={260}
            style={{
              borderRight: "1px solid #E8E9EB",
              boxShadow: "none",
            }}
          >
            <Menu
              mode="inline"
              selectedKeys={[location.pathname]}
              onSelect={({ key }) => onSelectMenu(key)}
              className={"!h-full text-size-3 text-color-2"}
              items={allMenuItems}
            />
          </Sider>
          <Layout>
            <Content
              style={{
                position: "relative",
                padding: 16,
                margin: 0,
                minHeight: 280,
                overflow: "auto",
              }}
            >
              <Outlet />
            </Content>
          </Layout>
        </Layout>
      </Layout>
      <ConfirmButton
        ref={logoutConfirmModalRef}
        buttonProps={{
          className: "hidden",
        }}
        okText={t("button.logout")}
        confirmMessage={t("message.confirmLogout")}
        handleOk={() => logout()}
      />
    </>
  );
};

export default ManagePageLayout;
